import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, Skeleton,
} from '@mui/material';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import Layout from '../../components/Layout/Layout';
import StatCard from '../../components/Common/StatCard';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import { getAdminDashboardApi } from '../../api/dashboard';
import { getBookFilterOptionsApi, getPublisherFilterOptionsApi } from '../../api/books';
import { TARGET_SEGMENTS_LIQVID } from '../../utils/constants';

const COLORS = ['#1565c0', '#f57c00', '#2e7d32', '#c62828', '#6a1b9a', '#00838f', '#4e342e', '#558b2f', '#00695c', '#ad1457'];


function PieLegend({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <Box sx={{ mt: 1.5, maxHeight: 130, overflowY: 'auto', pr: 0.5 }}>
      {data.map((entry, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Box sx={{ width: 11, height: 11, borderRadius: '2px', bgcolor: COLORS[i % COLORS.length], flexShrink: 0 }} />
          <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.3 }}>{entry.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
            {entry.count} ({total > 0 ? ((entry.count / total) * 100).toFixed(0) : 0}%)
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function LangPieCard({ title, total, chartData, accentColor }) {
  const langLabel = title.replace(' Language', '');
  const emptyMsg = langLabel
    ? `No ${langLabel} language books yet`
    : 'No books with language origin tagged yet';
  return (
    <Card sx={{ height: '100%', borderTop: `4px solid ${accentColor}` }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {total} books
        </Typography>
        {chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 150, textAlign: 'center', px: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {emptyMsg}
            </Typography>
          </Box>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={65}>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(val, name) => [val, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieLegend data={chartData} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PieCard({ title, chartData }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        {chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <Typography variant="body2" color="text.secondary">No data available</Typography>
          </Box>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(val, name) => [val, name]} />
              </PieChart>
            </ResponsiveContainer>
            <PieLegend data={chartData} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishers, setPublishers] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  useEffect(() => {
    getPublisherFilterOptionsApi().then((r) => setPublishers(r.data || [])).catch(() => {});
    getBookFilterOptionsApi().then((r) => {
      setDisciplines(r.data.disciplines || []);
      setLanguages(r.data.languages || []);
    }).catch(() => {});
  }, []);

  const fetchDashboard = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await getAdminDashboardApi(params);
    setData(r.data);
    setLoading(false);
  }, [search, filters]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const filterConfig = [
    { key: 'publisher', label: 'Publisher', type: 'select', options: publishers.map(p => ({ value: p.id, label: p.label })) },
    { key: 'discipline', label: 'Discipline', type: 'select', options: disciplines },
    { key: 'language', label: 'Language', type: 'select', options: languages },
    { key: 'target_segment_liqvid', label: 'Target Segment (Liqvid)', type: 'select', options: TARGET_SEGMENTS_LIQVID },
    { key: 'author_name', label: 'Author', type: 'text' },
  ];

  if (loading) return (
    <Layout title="Admin Dashboard">
      <PageHeader title="Admin Dashboard" subtitle="Overview of the Book Publishing Portal" />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Grid item xs={12} sm={4} md={4} key={i}>
            <Card><CardContent><Skeleton variant="text" width="60%" /><Skeleton variant="rectangular" height={48} sx={{ my: 1 }} /><Skeleton variant="text" width="40%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card><CardContent><Skeleton variant="text" width="50%" sx={{ mb: 1 }} /><Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', my: 2 }} /><Skeleton variant="text" /><Skeleton variant="text" width="80%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[...Array(2)].map((_, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card><CardContent><Skeleton variant="text" width="50%" sx={{ mb: 1 }} /><Skeleton variant="rectangular" height={200} /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
    </Layout>
  );

  const { summary, books_by_publisher, latest_publishers } = data || {};

  const toChartData = (arr, key) => (arr || []).map(item => ({ name: item[key] || 'Unknown', count: item.count }));

  const langOriginData = toChartData(data?.books_by_language_origin, 'language_origin');
  const langOriginTotal = langOriginData.reduce((s, d) => s + d.count, 0);
  const foreignData = toChartData(data?.books_by_foreign_lang, 'language');
  const indianData = toChartData(data?.books_by_indian_lang, 'language');
  const targetSegData = toChartData(data?.books_by_target_segment_liqvid, 'target_segment_liqvid');
  const disciplineData = toChartData(data?.books_by_discipline, 'discipline');

  return (
    <Layout title="Admin Dashboard">
      <PageHeader title="Admin Dashboard" subtitle="Overview of the Book Publishing Portal" />

      <SearchFilterBar
        searchPlaceholder="Search by ISBN, title, author, discipline..."
        filters={filterConfig}
        onSearch={setSearch}
        onFilter={setFilters}
      />

      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard title="Total Books" value={summary?.total_books} icon={<LibraryBooksIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard title="Total Publishers" value={summary?.total_publishers} icon={<BusinessIcon />} color="secondary" />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <StatCard title="Total End Users" value={summary?.total_users} icon={<PeopleIcon />} color="success" />
        </Grid>
      </Grid>


      {/* Language cards row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <LangPieCard
            title="Language"
            total={langOriginTotal}
            chartData={langOriginData}
            accentColor="#1565c0"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <LangPieCard
            title="Foreign Language"
            total={data?.books_by_foreign_count || 0}
            chartData={foreignData}
            accentColor="#f57c00"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <LangPieCard
            title="Indian Language"
            total={data?.books_by_indian_count || 0}
            chartData={indianData}
            accentColor="#2e7d32"
          />
        </Grid>
      </Grid>

      {/* Books by Target Segment + Books by Discipline */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <PieCard title="Books by Target Segment" chartData={targetSegData} />
        </Grid>
        <Grid item xs={12} md={6}>
          <PieCard title="Books by Discipline" chartData={disciplineData} />
        </Grid>
      </Grid>

      {/* Books by Group Owner + Latest Publishers */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Books by Group Owner</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={books_by_publisher?.map(b => ({ name: b.publisher__publisher_profile__company_name || b.publisher__name, count: b.count }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1565c0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Latest Publishers</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {latest_publishers?.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.publisher_profile__company_name}</TableCell>
                      <TableCell>
                        <Chip label={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? 'success' : 'default'} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
}
